/* /assets/interactive-map.js
 * Interactive Mapbox map for the "100 People" landing page.
*/

(function () {
  'use strict';

  function escapeHTML(str) {
    return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  function createMarkerEl(user) {
    var el = document.createElement('div');
    el.className = 'interactive-map__marker';
    if (user.photo) el.style.backgroundImage = 'url("' + user.photo + '")';
    el.setAttribute('role', 'button');
    el.setAttribute('tabindex', '0');
    el.setAttribute('aria-label', user.name + ', ' + user.location + ', ' + (user.year || ''));
    return el;
  }

  function buildHoverPopupHTML(user) {
    var photo = user.photo
      ? '<div class="interactive-map__popup-photo" style="background-image:url(\'' +
        String(user.photo).replace(/'/g, "\\'") + '\')"></div>'
      : '<div class="interactive-map__popup-photo"></div>';
    return '<div class="interactive-map__popup">' + photo +
      '<div class="interactive-map__popup-info">' +
      '<p class="interactive-map__popup-name">' + escapeHTML(user.name) + '</p>' +
      '<p class="interactive-map__popup-meta">' +
      escapeHTML(user.location) + ' &middot; ' + escapeHTML(String(user.year)) +
      '</p></div></div>';
  }

  function addUserToMap(map, user, modal) {
    var el = createMarkerEl(user);
    var hoverPopup = new mapboxgl.Popup({
      offset: 18,
      closeButton: false,
      closeOnClick: false,
      className: 'interactive-map__hover-popup'
    }).setHTML(buildHoverPopupHTML(user));

    var marker = new mapboxgl.Marker({ element: el, anchor: 'center' })
      .setLngLat([user.lng, user.lat])
      .addTo(map);

    el.addEventListener('mouseenter', function () {
      hoverPopup.setLngLat([user.lng, user.lat]).addTo(map);
    });
    el.addEventListener('mouseleave', function () {
      hoverPopup.remove();
    });
    el.addEventListener('click', function (e) {
      e.stopPropagation();
      hoverPopup.remove();
      if (modal) modal.open(user);
    });
    el.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        if (modal) modal.open(user);
      }
    });

    return marker;
  }

  function whenMapboxReady(cb, onFail, attempts) {
    attempts = attempts == null ? 80 : attempts;
    if (typeof window.mapboxgl !== 'undefined') { cb(); return; }
    if (attempts <= 0) {
      console.warn('[interactive-map] mapbox-gl failed to load.');
      if (onFail) onFail();
      return;
    }
    setTimeout(function () { whenMapboxReady(cb, onFail, attempts - 1); }, 100);
  }

  // Shown instead of a blank map when the Mapbox library never arrives
  // (typically a consent tool blocking the script until cookies are accepted).
  function showMapUnavailable(section, mapEl) {
    var text = section.getAttribute('data-msg-map-unavailable') ||
      'The map could not be loaded. Please allow cookies for this site and reload the page.';
    var note = document.createElement('p');
    note.className = 'interactive-map__unavailable';
    note.setAttribute('role', 'status');
    note.textContent = text;
    mapEl.appendChild(note);
  }

  function init(section) {
    if (section.dataset.interactiveMapInit === 'true') return;
    var token = section.dataset.mapboxToken;
    var mapEl = section.querySelector('[data-interactive-map]');
    if (!mapEl || !token) {
      console.warn('[interactive-map] Missing map container or Mapbox token.');
      return;
    }
    whenMapboxReady(
      function () { boot(section, mapEl, token); },
      function () { showMapUnavailable(section, mapEl); }
    );
  }

  function boot(section, mapEl, token) {
    section.dataset.interactiveMapInit = 'true';

    var style = section.dataset.mapStyle || 'mapbox://styles/mapbox/light-v11';
    var lat = parseFloat(section.dataset.initialLat);
    var lng = parseFloat(section.dataset.initialLong);
    var zoom = parseFloat(section.dataset.initialZoom);

    if (isNaN(lat)) lat = 64;
    if (isNaN(lng)) lng = -18;
    if (isNaN(zoom)) zoom = 2;

    mapboxgl.accessToken = token;

    // If WebGL is unavailable the Map constructor throws. Keep going so the
    // form (with address autocomplete) still works without the map.
    var map = null;
    try {
      map = new mapboxgl.Map({
        container: mapEl,
        style: style,
        center: [lng, lat],
        zoom: zoom,
        attributionControl: true
      });
      map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'top-right');
    } catch (err) {
      console.error('[interactive-map] Could not initialise the map.', err);
    }

    var apiBase = (section.dataset.apiBase || '').replace(/\/$/, '');
    var modal = createUserModal(section);
    var users = [];

    section._interactiveMap = {
      map: map,
      users: users,
      modal: modal,
      apiBase: apiBase,
      addUser: function (user) {
        users.push(user);
        if (map) addUserToMap(map, user, modal);
      }
    };

    if (map) map.on('load', function () {
      addLatitudeLine(section, map);
      if (apiBase) {
        loadFromBackend(apiBase).then(function (people) {
          people.forEach(function (p) { section._interactiveMap.addUser(p); });
        }).catch(function (err) {
          console.error('[interactive-map] Failed to load people from backend.', err);
        });
      }
    });

    bindLocationAutocomplete(section, token);
    bindMapPicker(section, token);
    bindForm(section);
    bindPanelToggle(section);
  }

  /* ---------- 66° latitude line (Arctic Circle) ---------- */
  // 66°North is named after the Arctic Circle, which sits at 66°33′N.
  var LATITUDE_LINE_DEG = 66.5622;

  function buildLatitudeLineGeoJSON(lat) {
    // One vertex per degree of longitude so the line follows the globe's
    // curvature instead of being drawn as a chord.
    var coords = [];
    for (var lng = -180; lng <= 180; lng += 1) coords.push([lng, lat]);
    return {
      type: 'Feature',
      properties: {},
      geometry: { type: 'LineString', coordinates: coords }
    };
  }

  function addLatitudeLine(section, map) {
    if (section.dataset.showLatitudeLine === 'false') return;
    var color = section.dataset.latitudeLineColor || '#1d3a8a';
    var label = section.dataset.latitudeLineLabel || '66°N';

    if (map.getSource('interactive-map-66n')) return;
    map.addSource('interactive-map-66n', {
      type: 'geojson',
      data: buildLatitudeLineGeoJSON(LATITUDE_LINE_DEG)
    });

    // Insert below the first symbol layer so country labels stay legible.
    var beforeId;
    var layers = map.getStyle().layers || [];
    for (var i = 0; i < layers.length; i++) {
      if (layers[i].type === 'symbol') { beforeId = layers[i].id; break; }
    }

    map.addLayer({
      id: 'interactive-map-66n-line',
      type: 'line',
      source: 'interactive-map-66n',
      layout: { 'line-cap': 'round', 'line-join': 'round' },
      paint: {
        'line-color': color,
        'line-width': ['interpolate', ['linear'], ['zoom'], 1, 1.5, 4, 2.5, 8, 3],
        'line-opacity': 0.9
      }
    }, beforeId);

    if (label) {
      map.addLayer({
        id: 'interactive-map-66n-label',
        type: 'symbol',
        source: 'interactive-map-66n',
        layout: {
          'symbol-placement': 'line',
          'symbol-spacing': 400,
          'text-field': label,
          'text-size': ['interpolate', ['linear'], ['zoom'], 1, 10, 4, 12, 8, 14],
          'text-font': ['DIN Pro Bold', 'Arial Unicode MS Bold'],
          'text-letter-spacing': 0.1,
          'text-offset': [0, -0.9],
          'text-keep-upright': true,
          'text-allow-overlap': false
        },
        paint: {
          'text-color': color,
          'text-halo-color': '#ffffff',
          'text-halo-width': 1.5
        }
      });
    }
  }

  /* ---------- Map click → location autofill + preview pin ---------- */
  function setLocationOnSection(section, lng, lat, label) {
    var input = section.querySelector('[data-interactive-map-location-input]');
    var hName = section.querySelector('[data-interactive-map-location-name]');
    var hLng  = section.querySelector('[data-interactive-map-location-lng]');
    var hLat  = section.querySelector('[data-interactive-map-location-lat]');
    var labelText = label || (lat.toFixed(4) + ', ' + lng.toFixed(4));
    if (input) input.value = labelText;
    if (hName) hName.value = labelText;
    if (hLng)  hLng.value = String(lng);
    if (hLat)  hLat.value = String(lat);
  }

  function showPreviewMarker(section, lng, lat) {
    var instance = section._interactiveMap;
    if (!instance || !instance.map) return;

    if (!instance.previewMarker) {
      var el = document.createElement('div');
      el.className = 'interactive-map__preview-marker';
      el.innerHTML =
        '<svg width="30" height="40" viewBox="0 0 30 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
          '<path d="M15 0C6.7 0 0 6.7 0 15c0 11 15 25 15 25s15-14 15-25C30 6.7 23.3 0 15 0z" class="interactive-map__preview-marker-body"/>' +
          '<circle cx="15" cy="15" r="5.5" fill="#fff"/>' +
        '</svg>';
      instance.previewMarker = new mapboxgl.Marker({
        element: el,
        anchor: 'bottom',
        draggable: true
      }).setLngLat([lng, lat]).addTo(instance.map);

      instance.previewMarker.on('dragend', function () {
        var ll = instance.previewMarker.getLngLat();
        reverseGeocode(instance.token, ll.lng, ll.lat).then(function (label) {
          setLocationOnSection(section, ll.lng, ll.lat, label);
        }).catch(function () {
          setLocationOnSection(section, ll.lng, ll.lat, '');
        });
      });
    } else {
      instance.previewMarker.setLngLat([lng, lat]);
    }
  }

  function clearPreviewMarker(section) {
    var instance = section._interactiveMap;
    if (instance && instance.previewMarker) {
      instance.previewMarker.remove();
      instance.previewMarker = null;
    }
  }

  function reverseGeocode(token, lng, lat) {
    var url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
      lng + ',' + lat + '.json?access_token=' + encodeURIComponent(token) +
      '&types=address,poi,place,locality,neighborhood,postcode&limit=1';
    return fetch(url).then(function (res) { return res.json(); }).then(function (data) {
      if (data && data.features && data.features[0]) {
        return data.features[0].place_name;
      }
      return '';
    });
  }

  function bindMapPicker(section, token) {
    var instance = section._interactiveMap;
    if (!instance || !instance.map) return;
    instance.token = token;

    instance.map.getCanvas().style.cursor = 'crosshair';

    instance.map.on('click', function (e) {
      var lng = e.lngLat.lng;
      var lat = e.lngLat.lat;
      showPreviewMarker(section, lng, lat);
      reverseGeocode(token, lng, lat).then(function (label) {
        setLocationOnSection(section, lng, lat, label);
      }).catch(function () {
        setLocationOnSection(section, lng, lat, '');
      });

      // Auto-open the panel if collapsed so user sees the autofilled value.
      var panel = section.querySelector('[data-interactive-map-panel]');
      var toggle = section.querySelector('[data-interactive-map-panel-toggle]');
      if (panel && panel.classList.contains('is-collapsed')) {
        panel.classList.remove('is-collapsed');
        if (toggle) toggle.setAttribute('aria-expanded', 'true');
      }
    });
  }

  /* ---------- Backend integration ---------- */
  function loadFromBackend(apiBase) {
    return fetch(apiBase + '/people', { credentials: 'omit' })
      .then(function (res) {
        if (!res.ok) throw new Error('GET /people failed: ' + res.status);
        return res.json();
      })
      .then(function (body) {
        var list = Array.isArray(body) ? body
          : (body && Array.isArray(body.data)) ? body.data
          : (body && Array.isArray(body.people)) ? body.people
          : [];
        return list.map(normalizePerson).filter(function (p) {
          return p && !isNaN(p.lng) && !isNaN(p.lat) && p.approved;
        });
      });
  }

  function normalizePerson(p) {
    if (!p) return null;
    var loc = p.location;
    var lat = NaN, lng = NaN, label = '';
    if (loc && typeof loc === 'object') {
      lat = parseFloat(loc.lat);
      lng = parseFloat(loc.lng != null ? loc.lng : loc.lon);
      label = loc.label || loc.name || '';
    } else if (typeof loc === 'string') {
      label = loc;
    }
    if (isNaN(lat)) lat = parseFloat(p.lat != null ? p.lat : p.latitude);
    if (isNaN(lng)) lng = parseFloat(p.lng != null ? p.lng : (p.longitude != null ? p.longitude : p.lon));

    return {
      id: p.id != null ? String(p.id) : ('person-' + Math.random()),
      name: p.name || '',
      year: p.year_of_birth != null ? p.year_of_birth : (p.year != null ? p.year : ''),
      decade: p.purchase_decade != null ? p.purchase_decade : (p.decade != null ? p.decade : ''),
      location: label,
      photo: p.image_url || p.photo_url || p.photo || p.image || '',
      lng: lng,
      lat: lat,
      approved: p.approved_at != null && p.approved_at !== ''
    };
  }

  function submitToBackend(apiBase, payload) {
    var fd = new FormData();
    if (payload.name) fd.append('name', payload.name);
    if (payload.email) fd.append('email', payload.email);
    fd.append('year_of_birth', String(payload.year));
    if (payload.decade) fd.append('purchase_decade', String(payload.decade));
    if (payload.gender) fd.append('gender', payload.gender);
    if (payload.styleName) fd.append('style_name', payload.styleName);
    fd.append('newsletter', payload.newsletter ? '1' : '0');
    fd.append('location', JSON.stringify({
      lat: payload.lat,
      lng: payload.lng,
      label: payload.location
    }));
    if (payload.file) fd.append('image', payload.file);
    fd.append('terms', payload.terms ? '1' : '0');

    return fetch(apiBase + '/people', {
      method: 'POST',
      body: fd,
      credentials: 'omit'
    }).then(function (res) {
      return res.text().then(function (text) {
        var body = null;
        try { body = text ? JSON.parse(text) : null; } catch (e) { /* not json */ }
        if (!res.ok) {
          var msg = (body && body.error) || text || ('HTTP ' + res.status);
          throw new Error(msg);
        }
        return body;
      });
    });
  }

  /* ---------- Click modal ---------- */
  function createUserModal(section) {
    var overlay = document.createElement('div');
    overlay.className = 'interactive-map__modal';
    overlay.hidden = true;
    overlay.innerHTML =
      '<div class="interactive-map__modal-backdrop" data-interactive-map-modal-close></div>' +
      '<div class="interactive-map__modal-dialog" role="dialog" aria-modal="true">' +
        '<button type="button" class="interactive-map__modal-close" aria-label="Close" data-interactive-map-modal-close>&times;</button>' +
        '<div class="interactive-map__modal-image" data-interactive-map-modal-image></div>' +
        '<div class="interactive-map__modal-info">' +
          '<h3 class="interactive-map__modal-name" data-interactive-map-modal-name></h3>' +
          '<p class="interactive-map__modal-meta" data-interactive-map-modal-meta></p>' +
        '</div>' +
      '</div>';
    section.appendChild(overlay);

    var imageEl = overlay.querySelector('[data-interactive-map-modal-image]');
    var nameEl  = overlay.querySelector('[data-interactive-map-modal-name]');
    var metaEl  = overlay.querySelector('[data-interactive-map-modal-meta]');

    function close() {
      overlay.hidden = true;
      document.removeEventListener('keydown', onKey);
    }
    function onKey(e) { if (e.key === 'Escape') close(); }

    overlay.querySelectorAll('[data-interactive-map-modal-close]').forEach(function (btn) {
      btn.addEventListener('click', close);
    });

    return {
      open: function (user) {
        imageEl.style.backgroundImage = user.photo ? 'url("' + user.photo + '")' : '';
        nameEl.textContent = user.name || '';
        var meta = user.location || '';
        var year = parseInt(user.year, 10);
        if (year > 0) {
          var bornLabel = section.getAttribute('data-label-born') || 'Born in {year}';
          meta += (meta ? ' \u00b7 ' : '') + bornLabel.replace('{year}', String(year));
        }
        if (user.decade) meta += (meta ? ' \u00b7 ' : '') + '66\u00b0North from the ' + user.decade + 's';
        metaEl.textContent = meta;
        overlay.hidden = false;
        document.addEventListener('keydown', onKey);
      },
      close: close
    };
  }

  /* ---------- Custom location autocomplete (Mapbox Geocoding REST API) ---------- */
  function bindLocationAutocomplete(section, token) {
    var input = section.querySelector('[data-interactive-map-location-input]');
    var list  = section.querySelector('[data-interactive-map-suggestions]');
    var hName = section.querySelector('[data-interactive-map-location-name]');
    var hLng  = section.querySelector('[data-interactive-map-location-lng]');
    var hLat  = section.querySelector('[data-interactive-map-location-lat]');
    if (!input || !list) return;

    var debounceTimer = null;
    var lastResults = [];
    var activeIndex = -1;

    function clearSelection() {
      hName.value = ''; hLng.value = ''; hLat.value = '';
    }

    function renderSuggestions(results) {
      list.innerHTML = '';
      activeIndex = -1;
      if (!results || !results.length) {
        list.hidden = true;
        return;
      }
      results.forEach(function (r, i) {
        var li = document.createElement('li');
        li.className = 'interactive-map__suggestion';
        li.textContent = r.place_name;
        li.dataset.index = String(i);
        li.addEventListener('mousedown', function (e) {
          e.preventDefault();
          select(i);
        });
        list.appendChild(li);
      });
      list.hidden = false;
    }

    function select(i) {
      var r = lastResults[i];
      if (!r) return;
      setLocationOnSection(section, r.center[0], r.center[1], r.place_name);
      showPreviewMarker(section, r.center[0], r.center[1]);
      var instance = section._interactiveMap;
      if (instance && instance.map) {
        instance.map.flyTo({
          center: [r.center[0], r.center[1]],
          zoom: Math.max(instance.map.getZoom(), 6)
        });
      }
      list.hidden = true;
    }

    function fetchSuggestions(q) {
      var url = 'https://api.mapbox.com/geocoding/v5/mapbox.places/' +
        encodeURIComponent(q) + '.json' +
        '?access_token=' + encodeURIComponent(token) +
        '&autocomplete=true&limit=6' +
        '&types=address,poi,place,locality,neighborhood,postcode';
      return fetch(url).then(function (res) { return res.json(); });
    }

    input.addEventListener('input', function () {
      clearSelection();
      var q = input.value.trim();
      if (debounceTimer) clearTimeout(debounceTimer);
      if (q.length < 2) {
        lastResults = [];
        renderSuggestions([]);
        return;
      }
      debounceTimer = setTimeout(function () {
        fetchSuggestions(q).then(function (data) {
          lastResults = (data && data.features) || [];
          renderSuggestions(lastResults);
        }).catch(function () {
          lastResults = [];
          renderSuggestions([]);
        });
      }, 250);
    });

    input.addEventListener('keydown', function (e) {
      if (list.hidden || !lastResults.length) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        activeIndex = Math.min(activeIndex + 1, lastResults.length - 1);
        updateActive();
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        activeIndex = Math.max(activeIndex - 1, 0);
        updateActive();
      } else if (e.key === 'Enter') {
        if (activeIndex >= 0) {
          e.preventDefault();
          select(activeIndex);
        }
      } else if (e.key === 'Escape') {
        list.hidden = true;
      }
    });

    function updateActive() {
      var items = list.querySelectorAll('.interactive-map__suggestion');
      items.forEach(function (el, i) {
        el.classList.toggle('is-active', i === activeIndex);
      });
    }

    input.addEventListener('blur', function () {
      // Delay so click can register; mousedown on items already calls select.
      setTimeout(function () { list.hidden = true; }, 150);
    });

    input.addEventListener('focus', function () {
      if (lastResults.length) list.hidden = false;
    });
  }

  function bindPanelToggle(section) {
    var panel = section.querySelector('[data-interactive-map-panel]');
    var toggle = section.querySelector('[data-interactive-map-panel-toggle]');
    if (!panel || !toggle) return;

    // Collapse by default on mobile.
    if (window.matchMedia && window.matchMedia('(max-width: 640px)').matches) {
      panel.classList.add('is-collapsed');
      toggle.setAttribute('aria-expanded', 'false');
    }

    toggle.addEventListener('click', function () {
      var collapsed = panel.classList.toggle('is-collapsed');
      toggle.setAttribute('aria-expanded', collapsed ? 'false' : 'true');
    });
  }

  function bindForm(section) {
    var form = section.querySelector('[data-interactive-map-form]');
    if (!form) return;

    var fileInput = form.querySelector('[data-interactive-map-file]');
    var fileHint = form.querySelector('[data-interactive-map-file-hint]');
    var message = form.querySelector('[data-interactive-map-message]');
    var locInput = form.querySelector('[data-interactive-map-location-input]');
    var yearInput = form.querySelector('[data-interactive-map-year]');
    var yearError = form.querySelector('[data-interactive-map-year-error]');
    var emailInput = form.querySelector('[data-interactive-map-email]');
    var emailError = form.querySelector('[data-interactive-map-email-error]');
    var decadeInput = form.querySelector('[data-interactive-map-decade]');
    var decadeToggle = form.querySelector('[data-interactive-map-decade-toggle]');
    var decadeText = form.querySelector('[data-interactive-map-decade-text]');
    var genderInput = form.querySelector('[data-interactive-map-gender]');
    var genderToggle = form.querySelector('[data-interactive-map-gender-toggle]');
    var genderText = form.querySelector('[data-interactive-map-gender-text]');
    var newsletterCheckbox = form.querySelector('[data-interactive-map-newsletter]');
    var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    var minAge = parseInt(section.getAttribute('data-min-age'), 10);
    if (!isFinite(minAge) || minAge < 0) minAge = 13;

    function msg(name, fallback) {
      var v = section.getAttribute('data-msg-' + name);
      return (v != null && v !== '') ? v : fallback;
    }
    function fmt(text, vars) {
      return String(text).replace(/\{(\w+)\}/g, function (_, k) {
        return Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : '{' + k + '}';
      });
    }
    function setYearError(text) {
      if (!yearError) return;
      if (text) {
        yearError.textContent = text;
        yearError.hidden = false;
        if (yearInput) yearInput.classList.add('is-invalid');
      } else {
        yearError.textContent = '';
        yearError.hidden = true;
        if (yearInput) yearInput.classList.remove('is-invalid');
      }
    }
    function setEmailError(text) {
      if (!emailError) return;
      if (text) {
        emailError.textContent = text;
        emailError.hidden = false;
        if (emailInput) emailInput.classList.add('is-invalid');
      } else {
        emailError.textContent = '';
        emailError.hidden = true;
        if (emailInput) emailInput.classList.remove('is-invalid');
      }
    }
    function isValidEmail(v) {
      return v.length <= 254 && EMAIL_RE.test(v);
    }
    if (emailInput) {
      emailInput.addEventListener('input', function () { setEmailError(''); });
      var validateEmailLive = function () {
        var v = (emailInput.value || '').trim();
        if (v === '') { setEmailError(''); return; }
        setEmailError(isValidEmail(v) ? '' : msg('email-invalid', 'Please enter a valid email address.'));
      };
      emailInput.addEventListener('blur', validateEmailLive);
      emailInput.addEventListener('change', validateEmailLive);
    }

    if (yearInput) {
      // Clear any prior inline error as soon as the user starts editing.
      yearInput.addEventListener('input', function () { setYearError(''); });

      // Live-validate when the user leaves the field, so they don't need to
      // submit to discover the year is invalid or below the minimum age.
      var validateYearLive = function () {
        var v = (yearInput.value || '').trim();
        if (v === '') { setYearError(''); return; }
        var thisYear = new Date().getFullYear();
        var y = parseInt(v, 10);
        if (!/^\d{4}$/.test(v) || y < 1900 || y > thisYear) {
          setYearError(msg('year-invalid', 'Please enter a valid year of birth.'));
          return;
        }
        if (thisYear - y < minAge) {
          setYearError(fmt(msg('age-too-young', 'You must be at least {age} years old to submit.'), { age: minAge }));
          return;
        }
        setYearError('');
      };
      yearInput.addEventListener('blur', validateYearLive);
      yearInput.addEventListener('change', validateYearLive);
    }

    // The gender and decade fields are the theme's <popout-select>: it writes
    // the chosen data-value into the hidden input and fires "change" on it.
    function resetPopout(input, toggle, text) {
      if (input) input.value = '';
      if (text) {
        text.textContent = text.getAttribute('data-placeholder') || '';
        text.classList.add('is-placeholder');
      }
      if (toggle) toggle.classList.remove('is-invalid');
    }
    function resetPopouts() {
      resetPopout(genderInput, genderToggle, genderText);
      resetPopout(decadeInput, decadeToggle, decadeText);
      form.querySelectorAll('.interactive-map__popout .popout-list__item.is-active').forEach(function (li) {
        li.classList.remove('is-active');
      });
    }
    // The popout writes the option's data-value (e.g. "non_binary") into the
    // toggle text; swap it for the option's visible label.
    function wirePopout(input, toggle, text) {
      if (!input) return;
      input.addEventListener('change', function () {
        if (text) {
          text.classList.toggle('is-placeholder', !input.value);
          var picked = null;
          if (input.value) {
            input.closest('popout-select').querySelectorAll('[data-popout-option]').forEach(function (opt) {
              if (opt.getAttribute('data-value') === input.value) picked = opt;
            });
          }
          if (picked) text.textContent = picked.textContent.trim();
        }
        if (toggle) toggle.classList.remove('is-invalid');
      });
    }
    wirePopout(genderInput, genderToggle, genderText);
    wirePopout(decadeInput, decadeToggle, decadeText);

    if (fileInput && fileHint) {
      var noFileLabel = section.getAttribute('data-label-no-file') || 'No file chosen';
      fileInput.addEventListener('change', function () {
        if (fileInput.files && fileInput.files[0]) {
          fileHint.textContent = fileInput.files[0].name;
        } else {
          fileHint.textContent = noFileLabel;
        }
      });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();

      var data = new FormData(form);
      var name = (data.get('name') || '').toString().trim();
      var email = (data.get('email') || '').toString().trim().toLowerCase();
      // Popout stores e.g. "1990s"; the backend wants the number.
      var decadeRaw = (data.get('purchase_decade') || '').toString().trim();
      var decade = decadeRaw ? parseInt(decadeRaw, 10) : '';
      var gender = (data.get('gender') || '').toString().trim();
      var styleName = (data.get('style_name') || '').toString().trim();
      var newsletter = !!(newsletterCheckbox && newsletterCheckbox.checked);
      var yearStr = (data.get('year') || '').toString().trim();
      var location = (data.get('location') || '').toString().trim();
      var lng = parseFloat(data.get('lng'));
      var lat = parseFloat(data.get('lat'));
      var file = data.get('photo');

      var thisYear = new Date().getFullYear();
      var year = parseInt(yearStr, 10);
      setYearError('');
      if (!/^\d{4}$/.test(yearStr) || year < 1900 || year > thisYear) {
        var yearMsg = msg('year-invalid', 'Please enter a valid year of birth.');
        setYearError(yearMsg);
        showMessage(message, yearMsg, true);
        if (yearInput) yearInput.focus();
        return;
      }
      if (thisYear - year < minAge) {
        var ageMsg = fmt(msg('age-too-young', 'You must be at least {age} years old to submit.'), { age: minAge });
        setYearError(ageMsg);
        showMessage(message, ageMsg, true);
        if (yearInput) yearInput.focus();
        return;
      }

      if (!name) {
        showMessage(message, msg('name-required', 'Please enter your name.'), true);
        return;
      }

      setEmailError('');
      if (!email || !isValidEmail(email)) {
        var emailMsg = msg('email-invalid', 'Please enter a valid email address.');
        setEmailError(emailMsg);
        showMessage(message, emailMsg, true);
        if (emailInput) emailInput.focus();
        return;
      }

      if (genderInput && !gender) {
        showMessage(message, msg('gender-required', 'Please select your gender.'), true);
        if (genderToggle) { genderToggle.classList.add('is-invalid'); genderToggle.focus(); }
        return;
      }

      if (decadeInput && !decade) {
        showMessage(message, msg('decade-required', 'Please select the decade you bought your 66°North piece.'), true);
        if (decadeToggle) { decadeToggle.classList.add('is-invalid'); decadeToggle.focus(); }
        return;
      }

      if (!file || !(file instanceof File) || file.size === 0) {
        showMessage(message, msg('photo-required', 'Please choose a photo.'), true);
        return;
      }

      if (!location || isNaN(lng) || isNaN(lat)) {
        showMessage(message, msg('location-required', 'Please pick a valid location from the dropdown.'), true);
        if (locInput) locInput.focus();
        return;
      }

      var termsCheckbox = form.querySelector('[data-interactive-map-terms]');
      if (!termsCheckbox || !termsCheckbox.checked) {
        showMessage(message, msg('terms-required', 'You must agree to the Terms of Service before submitting.'), true);
        if (termsCheckbox) termsCheckbox.focus();
        return;
      }

      var instance = section._interactiveMap;
      if (!instance) return;

      var submitBtn = form.querySelector('[type="submit"]');
      if (submitBtn) submitBtn.disabled = true;
      showMessage(message, msg('submitting', 'Submitting…'), false);

      var resetForm = function () {
        form.reset();
        setEmailError('');
        resetPopouts();
        if (fileHint) fileHint.textContent = section.getAttribute('data-label-no-file') || 'No file chosen';
        var hName = section.querySelector('[data-interactive-map-location-name]');
        var hLng  = section.querySelector('[data-interactive-map-location-lng]');
        var hLat  = section.querySelector('[data-interactive-map-location-lat]');
        if (hName) hName.value = '';
        if (hLng) hLng.value = '';
        if (hLat) hLat.value = '';
        clearPreviewMarker(section);
      };

      var addLocally = function (photoOverrideUrl) {
        instance.addUser({
          id: 'submission-' + Date.now(),
          name: name, year: year, decade: decade, location: location,
          photo: photoOverrideUrl || URL.createObjectURL(file),
          lng: lng, lat: lat
        });
        if (instance.map) instance.map.flyTo({
          center: [lng, lat],
          zoom: Math.max(instance.map.getZoom(), 6)
        });
      };

      if (instance.apiBase) {
        submitToBackend(instance.apiBase, {
          name: name, email: email, year: year, decade: decade,
          gender: gender, styleName: styleName,
          newsletter: newsletter, location: location,
          lng: lng, lat: lat, file: file,
          terms: true
        }).then(function (body) {
          var raw = body && body.data ? body.data : body;
          var person = raw ? normalizePerson(raw) : null;
          // Only add to the map immediately if the backend returned an approved entry.
          if (person && person.approved && !isNaN(person.lng) && !isNaN(person.lat)) {
            instance.addUser(person);
            if (instance.map) instance.map.flyTo({
              center: [person.lng, person.lat],
              zoom: Math.max(instance.map.getZoom(), 6)
            });
          }
          resetForm();
          showMessage(message, msg('success', 'Thanks! You have successfully signed up. Please wait for approval before you appear on the map.'), false);
        }).catch(function (err) {
          console.error('[interactive-map]', err);
          var prefix = msg('error-prefix', 'Could not save submission:');
          var detail = (err && err.message) ? err.message : 'unknown error';
          showMessage(message, prefix + ' ' + detail, true);
        }).then(function () {
          if (submitBtn) submitBtn.disabled = false;
        });
      } else {
        addLocally();
        resetForm();
        showMessage(message, 'Thanks! Your pin has been added (preview only).', false);
        if (submitBtn) submitBtn.disabled = false;
      }
    });
  }

  function showMessage(el, text, isError) {
    if (!el) return;
    el.textContent = text;
    el.classList.toggle('is-error', !!isError);
  }

  function initAll() {
    document
      .querySelectorAll('[data-section-type="interactive-map"]')
      .forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', function (e) {
    var section = e.target.querySelector('[data-section-type="interactive-map"]');
    if (section) init(section);
  });
})();
